/**
 * /knowledge — team knowledge bases (filesystem + ingestion + MCP).
 *
 * Identity comes from the dashboard-shell auth context; the loader only
 * fetches kb-specific state.
 */

import { useLoaderData } from "react-router-dom";

import { PageHeader } from "@/components/page-header";
import { KnowledgeBrowser } from "@/features/knowledge/knowledge-browser";
import { ApiError, api, apiRaw } from "@/lib/api";
import type { KbView } from "@/lib/types";

import { useAuthState } from "../lib/auth";

interface KnowledgeLoaderData {
  kbs: KbView[];
}

export async function knowledgeLoader(): Promise<KnowledgeLoaderData> {
  // The personal-KB bootstrap is idempotent and best-effort; race it with
  // the actual list so we don't pay an extra round-trip on every load. A
  // failure here just means the list might be one row short on the very
  // first visit — refresh fixes it.
  const [kbs] = await Promise.all([
    api<KbView[]>("/kbs"),
    apiRaw("/kbs/me/init", { method: "POST" }).catch((err: unknown) => {
      if (err instanceof ApiError) {
        console.warn(`[knowledge] /kbs/me/init failed: ${err.status} ${err.message}`);
      }
      return null;
    }),
  ]);
  return { kbs };
}

export default function KnowledgeRoute() {
  const data = useLoaderData() as KnowledgeLoaderData;
  const auth = useAuthState();
  const isAdmin = auth.tenant.role === "team_admin";
  return (
    <section className="space-y-4">
      <PageHeader
        eyebrow="workspace"
        title="Knowledge"
        subtitle="Team knowledge bases. Each KB has its own filesystem, ingestion pipeline, and (optional) MCP endpoint."
      />
      <KnowledgeBrowser initial={data} isAdmin={isAdmin} />
    </section>
  );
}
